/**
 * PDFAuthoringEditor.js
 *
 * Enhanced authoring UI with:
 * - Continuous scroll multi-page layout
 * - Real zoom with scale preservation
 * - Field interactions: move, resize, delete
 * - Click-to-place mode
 * - Right-click context menu
 * - Bottom-left anchor for drops
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist';
import PDFUploadArea from './PDFUploadArea';
import FieldCatalogSidebar from './FieldCatalogSidebar';
import VersionRulesPanel from './VersionRulesPanel';
import { dragStateStore } from '../utils/dragStateStore';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 20;

// Helper: Convert field key to human-readable label
// e.g., "current_date" → "Current Date", "owner_1_full_name" → "Owner 1 Full Name"
const formatFieldKeyAsLabel = (fieldKey) => {
  return fieldKey
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export default function PDFAuthoringEditor() {
  const { templateId, versionId } = useParams();
  const token = localStorage.getItem('token') || '';
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('template-authoring', '/funders');

  // State
  const [authoring, setAuthoring] = useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [fields, setFields] = useState([]);
  const [pageHeights, setPageHeights] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Undo/Redo history
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem('editorZoom');
    const parsed = saved ? parseFloat(saved) : 1;
    // Defensive check: ensure zoom is positive (prevents flip bug from negative zoom)
    return parsed > 0 ? parsed : 1;
  });
  const [selectedId, setSelectedId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [activating, setActivating] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving' | 'saved' | null
  const autoSaveTimeoutRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const autoSaveQueueRef = useRef(null);

  // Click-to-place mode: { field_key, label, render_type } or null
  const [placementMode, setPlacementMode] = useState(null);

  // Version metadata for editor chrome
  const [editingVersionName, setEditingVersionName] = useState(false);
  const [versionNameInput, setVersionNameInput] = useState('');
  const [updatingVersionName, setUpdatingVersionName] = useState(false);
  const [updatingArchive, setUpdatingArchive] = useState(false);

  // Drag state for moving placed fields
  const [draggedFieldId, setDraggedFieldId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingFieldId, setResizingFieldId] = useState(null);
  const [hoveredFieldId, setHoveredFieldId] = useState(null);  // Track hovered field for resize handle display
  const [canvasCursor, setCanvasCursor] = useState('default');
  const dragStateRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRefs = useRef({});
  const overlayCanvasRefs = useRef({}); // Separate overlay for transient drawing
  const canvasSizesRef = useRef({}); // Track overlay canvas sizes to prevent unnecessary resets
  const isRenderingRef = useRef(false); // CRITICAL: Prevent concurrent PDF renders
  const dragPreviewFieldsRef = useRef(null); // CRITICAL FIX: Temp field updates during drag (don't trigger render effect)

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);

  // Drag preview state for drag-from-sidebar
  const [dragPreview, setDragPreview] = useState(null);

  // Visual preview for moving existing fields (overlay canvas)
  const [fieldMovePreview, setFieldMovePreview] = useState(null);

  // Visual preview for resizing existing fields (overlay canvas)
  const [resizePreview, setResizePreview] = useState(null);

  // Click-to-place cursor position for live preview
  const [placementCursorPos, setPlacementCursorPos] = useState(null);

  // Marquee selection state
  const [marqueeStart, setMarqueeStart] = useState(null); // { pageNum, sx, sy }
  const [marqueeEnd, setMarqueeEnd] = useState(null);     // { pageNum, sx, sy }
  const [selectedFieldIds, setSelectedFieldIds] = useState([]); // Multiple selected fields
  const [clipboard, setClipboard] = useState(null); // { fields: [...], boundingBox: {...} }
  const [currentPageNum, setCurrentPageNum] = useState(1); // Track currently active page for paste

  // Persist zoom to localStorage
  useEffect(() => {
    localStorage.setItem('editorZoom', zoom.toString());
  }, [zoom]);

  // Update fields with undo history management
  const updateFields = useCallback((newFieldsOrUpdater) => {
    setFields(prevFields => {
      // Determine the new fields array
      const newFields = typeof newFieldsOrUpdater === 'function'
        ? newFieldsOrUpdater(prevFields)
        : newFieldsOrUpdater;

      // Add previous state to history
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
      historyRef.current.push(prevFields);
      historyIndexRef.current = historyRef.current.length - 1;

      // Keep only last 50 states in history (prevent memory bloat)
      if (historyRef.current.length > 50) {
        historyRef.current.shift();
        historyIndexRef.current--;
      }

      console.log(`📝 [FIELDS UPDATED] history now has ${historyRef.current.length} states`);
      return newFields;
    });
  }, []);

  // Undo last change (Ctrl+Z)
  const handleUndo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current--;
      const previousFields = historyRef.current[historyIndexRef.current];
      setFields(previousFields);
      console.log(`↩️  [UNDO] Restored to state ${historyIndexRef.current}, fields=${previousFields.length}`);
    } else {
      console.log('↩️  [UNDO] No history to undo');
    }
  }, []);

  // Keyboard listener for Ctrl+Z
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo]);

  // Helper: Normalize field for autosave (ensure all numeric values are valid numbers)
  const normalizeFieldForSave = (f) => {
    const x_pt = typeof f.x_pt === 'number' ? f.x_pt : parseFloat(f.x_pt) || 0;
    const y_pt = typeof f.y_pt === 'number' ? f.y_pt : parseFloat(f.y_pt) || 0;
    const width_pt = typeof f.width_pt === 'number' ? f.width_pt : parseFloat(f.width_pt) || 50;
    const height_pt = typeof f.height_pt === 'number' ? f.height_pt : parseFloat(f.height_pt) || 20;

    // Ensure no NaN or Infinity values
    const isValidNumber = (n) => typeof n === 'number' && isFinite(n);

    return {
      field_key: f.field_key,
      page_number: f.page_number,
      x_pt: isValidNumber(x_pt) ? x_pt : 0,
      y_pt: isValidNumber(y_pt) ? y_pt : 0,
      width_pt: isValidNumber(width_pt) ? width_pt : 50,
      height_pt: isValidNumber(height_pt) ? height_pt : 20,
    };
  };

  // Debounced autosave
  const triggerAutosave = useCallback((fieldsToSave) => {
    console.log('🔄 Autosave triggered with', fieldsToSave.length, 'fields');
    setAutoSaveStatus('saving');

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        const payload = fieldsToSave.map(normalizeFieldForSave);

        console.log('📤 Sending autosave payload with', payload.length, 'fields');
        const res = await axios.put(
          `${API_URL}/contract-templates/${templateId}/versions/${versionId}/authoring`,
          { fields: payload },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('✅ Autosave successful, received', res.data.fields.length, 'fields from server');
        setAuthoring(res.data);
        setAutoSaveStatus('saved');

        // Hide 'Saved' message after 2 seconds
        setTimeout(() => setAutoSaveStatus(null), 2000);
      } catch (e) {
        console.error('❌ Autosave failed:', e.response?.data || e.message);
        setAutoSaveStatus(null);
      }
    }, 800); // 800ms debounce delay
  }, [templateId, versionId, token]);

  // Define loadPDF before it's used
  const loadPDF = useCallback(async (pdfUrl) => {
    try {
      const res = await fetch(API_URL + pdfUrl, {
        cache: 'reload',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const buf = await res.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      setPdfDoc(pdf);
    } catch (e) {
      setError(`PDF load failed: ${e.message}`);
      throw e;
    }
  }, [token]);

  // Load authoring on mount or param change
  useEffect(() => {
    if (!templateId || !versionId) return;

    const load = async () => {
      try {
        setLoading(true);
        console.log('🔍 [LOAD AUTHORING] Fetching data for template:', templateId, 'version:', versionId);
        const res = await axios.get(
          `${API_URL}/contract-templates/${templateId}/versions/${versionId}/authoring`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        console.log('✅ [LOAD AUTHORING] Data loaded');
        console.log('📋 [LOAD AUTHORING] applicability_rules_json:', res.data.applicability_rules_json);

        setAuthoring(res.data);
        setTemplateType(res.data.template_type);
        setFields(res.data.fields);  // Initial load, don't add to history

        const heights = {};
        res.data.pages.forEach(p => heights[p.page_number] = p.height_pt);
        setPageHeights(heights);

        if (res.data.pdf_file_path) {
          await loadPDF(res.data.pdf_file_path);
        }
        setError(null);
      } catch (e) {
        console.error('❌ [LOAD AUTHORING] Failed:', e);
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [templateId, versionId, token, loadPDF]);

  // Callback for when rules are saved in VersionRulesPanel
  // Updates the authoring object with new rules so they persist in this component's state
  const handleRulesSaved = useCallback((updatedRules) => {
    console.log('📋 [RULES SAVED] Updating authoring state with new rules:', updatedRules);
    setAuthoring(prev => prev ? { ...prev, applicability_rules_json: updatedRules } : prev);
  }, []);

  // Set up page header with back button
  useEffect(() => {
    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: 'Template Editor',
      subtitle: null,
      actions: [],
    });

    return () => resetHeaderConfig();
  }, [handleBack, setHeaderConfig, resetHeaderConfig]);

  // Render each page as its own canvas
  useEffect(() => {
    if (!pdfDoc || isRenderingRef.current) return;

    console.log(`🎬 [RENDER EFFECT TRIGGERED] fields=${fields.length}, zoom=${zoom}, pdfDoc.numPages=${pdfDoc.numPages}`);

    isRenderingRef.current = true;

    const renderAllPages = async () => {
      try {
        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
          const canvasRef = canvasRefs.current[pageNum];
          if (!canvasRef) {
            console.warn(`⚠️  Canvas ref missing for page ${pageNum}`);
            continue;
          }

          try {
            console.log(`  📄 Rendering page ${pageNum}...`);
            const page = await pdfDoc.getPage(pageNum);
            const vp = page.getViewport({ scale: zoom });

            // CRITICAL FIX: Set canvas size BEFORE getting context
            // Setting width/height clears canvas and resets context
            // Must get fresh context after size is set
            canvasRef.width = vp.width;
            canvasRef.height = vp.height;

            // Also set overlay canvas to same size (only if different to avoid clearing)
            const overlayCanvasRef = overlayCanvasRefs.current[pageNum];
            if (overlayCanvasRef) {
              const sizeKey = `${pageNum}`;
              const currentSize = canvasSizesRef.current[sizeKey];
              if (!currentSize || currentSize.width !== vp.width || currentSize.height !== vp.height) {
                overlayCanvasRef.width = vp.width;
                overlayCanvasRef.height = vp.height;
                canvasSizesRef.current[sizeKey] = { width: vp.width, height: vp.height };
                console.log(`  🎨 Overlay canvas ${pageNum} sized to ${vp.width}x${vp.height}`);
              }
            }

            const ctx = canvasRef.getContext('2d');

            // Render PDF to canvas
            await page.render({ canvasContext: ctx, viewport: vp }).promise;
            console.log(`    ✅ PDF rendered for page ${pageNum}`);

            // Draw fields for this page
            const pageH = pageHeights[pageNum] || 792;
            const fieldsOnPage = fields.filter(f => f.page_number === pageNum);
            console.log(`    📍 Page ${pageNum}: ${fieldsOnPage.length} fields, pageH=${pageH}`);

            fieldsOnPage.forEach(f => {
              const sx = f.x_pt * zoom;
              const sy = (pageH - (f.y_pt + f.height_pt)) * zoom;
              const sw = f.width_pt * zoom;
              const sh = f.height_pt * zoom;

              // Field background (light green)
              ctx.fillStyle = 'rgba(77, 219, 148, 0.1)';
              ctx.fillRect(sx, sy, sw, sh);

              // Field border: color varies by selection state
              const isMultiSelected = selectedFieldIds.includes(f.id);
              const isSingleSelected = selectedId === f.id;

              if (isSingleSelected) {
                // Single selection: dark green unselected, red/yellow selected
                const isCheckbox = f.render_type === 'checkbox' || f.width_pt < 10;
                ctx.strokeStyle = isCheckbox ? '#d32f2f' : '#ffd700';
              } else if (isMultiSelected) {
                // Multi selection: light blue
                ctx.strokeStyle = '#2196F3';
              } else {
                // Unselected: dark green
                ctx.strokeStyle = '#115845';
              }
              ctx.lineWidth = 2;
              ctx.strokeRect(sx, sy, sw, sh);

              // Field label (inside the box, dark green text) - use human-readable label
              ctx.fillStyle = '#115845';
              ctx.font = 'bold 11px Arial';
              // Use label, or format key as fallback (for previously saved fields)
              const displayLabel = f.label || formatFieldKeyAsLabel(f.field_key);
              ctx.fillText(displayLabel, sx + 4, sy + 14);

              // Resize handle at top-right corner (show on single selection or hover)
              if (isSingleSelected || hoveredFieldId === f.id) {
                const handleSize = 8;
                const hx = sx + sw - handleSize;
                const hy = sy - handleSize;
                // Checkboxes: red handle, other fields: yellow handle
                const isCheckbox = f.render_type === 'checkbox' || f.width_pt < 10;
                ctx.fillStyle = isCheckbox ? '#d32f2f' : '#ffd700';
                ctx.fillRect(hx, hy, handleSize, handleSize);
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 1;
                ctx.strokeRect(hx, hy, handleSize, handleSize);
              }
            });

          } catch (e) {
            console.error(`❌ Render page ${pageNum} error:`, e);
          }
        }
        console.log(`✅ [RENDER EFFECT COMPLETE] All pages rendered`);
      } finally {
        isRenderingRef.current = false;
      }
    };

    renderAllPages();
  }, [pdfDoc, zoom, fields, selectedId, selectedFieldIds, pageHeights, hoveredFieldId]);

  // Separate effect for transient drawing (previews, marquee)
  // This updates frequently without triggering expensive PDF re-renders
  useEffect(() => {
    if (!pdfDoc) return;

    console.log(`🎨 [TRANSIENT RENDER] dragPreview=${!!dragPreview}, placementMode=${!!placementMode}, marquee=${!!(marqueeStart && marqueeEnd)}`);

    // Clear all overlay canvases first (only if they have valid dimensions)
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const overlayCanvas = overlayCanvasRefs.current[pageNum];
      if (overlayCanvas && overlayCanvas.width > 0 && overlayCanvas.height > 0) {
        const ctx = overlayCanvas.getContext('2d');
        ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
      }
    }

    // Draw transient elements on appropriate pages
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const overlayCanvas = overlayCanvasRefs.current[pageNum];
      if (!overlayCanvas) continue;

      const ctx = overlayCanvas.getContext('2d');

      // Draw drag preview (field being dragged from sidebar)
      if (dragPreview && dragPreview.pageNum === pageNum) {
        const sx = dragPreview.sx;
        const sy = dragPreview.sy;
        const sw = dragPreview.width * zoom;
        const sh = dragPreview.height * zoom;

        console.log(`🎨 [DRAG PREVIEW RENDER] page ${pageNum}:`, {
          sx, sy, sw, sh,
          isFinite_sx: isFinite(sx),
          isFinite_sy: isFinite(sy),
          sw_positive: sw > 0,
          sh_positive: sh > 0,
        });

        // Validate dimensions (prevent flipped/invalid drawing)
        if (isFinite(sx) && isFinite(sy) && sw > 0 && sh > 0) {
          const previewTop = sy - sh / 2;
          const previewLeft = sx - sw / 2;

          ctx.fillStyle = 'rgba(255, 107, 107, 0.15)';
          ctx.fillRect(previewLeft, previewTop, sw, sh);

          ctx.strokeStyle = 'rgba(255, 107, 107, 0.8)';
          ctx.lineWidth = 2;
          ctx.strokeRect(previewLeft, previewTop, sw, sh);

          ctx.fillStyle = 'rgba(255, 107, 107, 1)';
          ctx.font = 'bold 11px Arial';
          ctx.fillText('dragging...', previewLeft + 3, previewTop + 14);

          console.log(`✅ [DRAG PREVIEW DRAWN] on page ${pageNum}`);
        } else {
          console.log(`❌ [DRAG PREVIEW INVALID] on page ${pageNum} - dimensions failed validation`);
        }
      }

      // Draw field move preview (existing field being moved)
      if (fieldMovePreview && fieldMovePreview.pageNum === pageNum) {
        const sx = fieldMovePreview.sx;
        const sy = fieldMovePreview.sy;
        const sw = fieldMovePreview.width * zoom;
        const sh = fieldMovePreview.height * zoom;

        console.log(`🎨 [FIELD MOVE PREVIEW RENDER] page ${pageNum}:`, {
          sx, sy, sw, sh,
          isFinite_sx: isFinite(sx),
          isFinite_sy: isFinite(sy),
          sw_positive: sw > 0,
          sh_positive: sh > 0,
        });

        // Validate dimensions
        if (isFinite(sx) && isFinite(sy) && sw > 0 && sh > 0) {
          // Adjust for height to match where field will actually be rendered
          // sy is the top-left screen position, but field rendering calculates: (pageH - (y_pt + height)) * zoom
          // So we need to subtract height to position preview correctly
          const previewTop = sy - sh;
          const previewLeft = sx;

          // Field move preview: blue outline with "moving..." text
          ctx.fillStyle = 'rgba(33, 150, 243, 0.15)';
          ctx.fillRect(previewLeft, previewTop, sw, sh);

          ctx.strokeStyle = 'rgba(33, 150, 243, 0.8)';
          ctx.lineWidth = 2;
          ctx.strokeRect(previewLeft, previewTop, sw, sh);

          ctx.fillStyle = 'rgba(33, 150, 243, 1)';
          ctx.font = 'bold 11px Arial';
          ctx.fillText('moving...', previewLeft + 3, previewTop + 14);

          console.log(`✅ [FIELD MOVE PREVIEW DRAWN] on page ${pageNum}`);
        } else {
          console.log(`❌ [FIELD MOVE PREVIEW INVALID] on page ${pageNum}`);
        }
      }

      // Draw resize preview (field being resized)
      if (resizePreview && resizePreview.pageNum === pageNum) {
        const sx = resizePreview.sx;
        const sy = resizePreview.sy;
        const sw = resizePreview.width * zoom;
        const sh = resizePreview.height * zoom;

        console.log(`🎨 [RESIZE PREVIEW RENDER] page ${pageNum}:`, {
          sx, sy, sw, sh,
          isFinite_sx: isFinite(sx),
          isFinite_sy: isFinite(sy),
          sw_positive: sw > 0,
          sh_positive: sh > 0,
        });

        // Validate dimensions
        if (isFinite(sx) && isFinite(sy) && sw > 0 && sh > 0) {
          // sy is already the screen top position, don't subtract sh
          const previewTop = sy;
          const previewLeft = sx;

          // Resize preview: purple outline with "resizing..." text (distinct from yellow selection)
          ctx.fillStyle = 'rgba(156, 39, 176, 0.15)';
          ctx.fillRect(previewLeft, previewTop, sw, sh);

          ctx.strokeStyle = 'rgba(156, 39, 176, 0.8)';
          ctx.lineWidth = 2;
          ctx.strokeRect(previewLeft, previewTop, sw, sh);

          ctx.fillStyle = 'rgba(156, 39, 176, 1)';
          ctx.font = 'bold 11px Arial';
          ctx.fillText('resizing...', previewLeft + 3, previewTop + 14);

          console.log(`✅ [RESIZE PREVIEW DRAWN] on page ${pageNum}`);
        } else {
          console.log(`❌ [RESIZE PREVIEW INVALID] on page ${pageNum}`);
        }
      }

      // Draw click-to-place preview (field following cursor)
      if (placementMode && placementCursorPos && placementCursorPos.pageNum === pageNum) {
        const sx = placementCursorPos.sx;
        const sy = placementCursorPos.sy;
        const sw = (placementMode.render_type === 'checkbox' ? 5 : DEFAULT_WIDTH) * zoom;
        const sh = (placementMode.render_type === 'checkbox' ? 10 : DEFAULT_HEIGHT) * zoom;

        // Validate dimensions (prevent flipped/invalid drawing)
        if (isFinite(sx) && isFinite(sy) && sw > 0 && sh > 0) {
          const previewTop = sy - sh / 2;
          const previewLeft = sx - sw / 2;

          ctx.fillStyle = 'rgba(255, 215, 0, 0.15)';
          ctx.fillRect(previewLeft, previewTop, sw, sh);

          ctx.strokeStyle = 'rgba(255, 215, 0, 0.9)';
          ctx.lineWidth = 2;
          ctx.strokeRect(previewLeft, previewTop, sw, sh);

          ctx.fillStyle = 'rgba(255, 215, 0, 1)';
          ctx.font = 'bold 11px Arial';
          ctx.fillText('click to place', previewLeft + 3, previewTop + 14);
        }
      }

      // Draw marquee selection rectangle
      if (marqueeStart && marqueeEnd && marqueeStart.pageNum === pageNum && marqueeEnd.pageNum === pageNum) {
        const minSx = Math.min(marqueeStart.sx, marqueeEnd.sx);
        const maxSx = Math.max(marqueeStart.sx, marqueeEnd.sx);
        const minSy = Math.min(marqueeStart.sy, marqueeEnd.sy);
        const maxSy = Math.max(marqueeStart.sy, marqueeEnd.sy);

        const width = maxSx - minSx;
        const height = maxSy - minSy;

        // Only draw if dimensions are valid
        if (width > 0 && height > 0 && isFinite(minSx) && isFinite(minSy)) {
          ctx.fillStyle = 'rgba(33, 150, 243, 0.1)';
          ctx.fillRect(minSx, minSy, width, height);

          ctx.strokeStyle = 'rgba(33, 150, 243, 0.8)';
          ctx.lineWidth = 2;
          ctx.strokeRect(minSx, minSy, width, height);
        }
      }
    }
  }, [pdfDoc, dragPreview, fieldMovePreview, resizePreview, placementMode, placementCursorPos, marqueeStart, marqueeEnd, zoom]);

  // Document-level drag preview updates for drag-from-sidebar
  // This handles mouse movement anywhere in the document, not just over the canvas
  useEffect(() => {
    if (!pdfDoc) return;

    const handleDocumentMouseMove = (e) => {
      // Only update drag preview if currently dragging from sidebar
      if (!dragStateStore.isActive || !dragStateStore.field || authoring?.is_active) {
        return;
      }

      console.log('📍 [GLOBAL MOUSEMOVE] drag active, finding page under cursor');

      // Find which canvas the cursor is over
      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        const canvas = canvasRefs.current[pageNum];
        if (!canvas) continue;

        const rect = canvas.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;

        // Check if cursor is over this canvas
        if (sx >= 0 && sx <= rect.width && sy >= 0 && sy <= rect.height) {
          console.log(`📍 [GLOBAL MOUSEMOVE] cursor over page ${pageNum}: sx=${sx}, sy=${sy}`);
          setDragPreview({
            pageNum,
            sx,
            sy,
            width: dragStateStore.field.width,
            height: dragStateStore.field.height,
            label: dragStateStore.field.label,
          });
          return;
        }
      }

      // If not over any canvas, still show preview if one exists
      if (dragPreview) {
        console.log('📍 [GLOBAL MOUSEMOVE] cursor not over canvas, keeping current preview');
      }
    };

    const handleDocumentMouseUp = (e) => {
      // End drag when mouse is released anywhere
      if (dragStateStore.isActive || dragStateStore.field) {
        console.log('☝️  [GLOBAL MOUSEUP] drag ended (released outside canvas)');
        dragStateStore.isActive = false;
        dragStateStore.field = null;
        setDragPreview(null);
      }
    };

    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [pdfDoc, authoring, dragPreview]);

  // Convert screen coords to PDF coords for a given page
  const screenToPdf = (pageNum, sx, sy, canvasRect) => {
    const pageH = pageHeights[pageNum] || 792;
    const xPt = sx / zoom;
    const yAtClick = pageH - (sy / zoom);
    return [xPt, yAtClick];
  };

  // Check if a point is over a field
  const getFieldAtScreenPoint = (pageNum, sx, sy) => {
    // eslint-disable-next-line no-unused-vars
    const pageH = pageHeights[pageNum] || 792;
    const [xPt, yPt] = screenToPdf(pageNum, sx, sy, null);

    for (const field of fields) {
      if (field.page_number !== pageNum) continue;

      const fieldLeft = field.x_pt;
      const fieldRight = field.x_pt + field.width_pt;
      const fieldBottom = field.y_pt;
      const fieldTop = field.y_pt + field.height_pt;

      if (xPt >= fieldLeft && xPt <= fieldRight && yPt >= fieldBottom && yPt <= fieldTop) {
        return field;
      }
    }
    return null;
  };

  // Helper: Check if a field overlaps with a selection rectangle (in screen coords)
  const getFieldsInMarquee = (pageNum, marqueeStart, marqueeEnd) => {
    if (!marqueeStart || !marqueeEnd) return [];

    const minSx = Math.min(marqueeStart.sx, marqueeEnd.sx);
    const maxSx = Math.max(marqueeStart.sx, marqueeEnd.sx);
    const minSy = Math.min(marqueeStart.sy, marqueeEnd.sy);
    const maxSy = Math.max(marqueeStart.sy, marqueeEnd.sy);

    return fields.filter(f => {
      if (f.page_number !== pageNum) return false;

      const pageH = pageHeights[pageNum] || 792;
      const sx_field = f.x_pt * zoom;
      const sy_field = (pageH - (f.y_pt + f.height_pt)) * zoom;
      const sx_field_end = sx_field + (f.width_pt * zoom);
      const sy_field_end = sy_field + (f.height_pt * zoom);

      // Check for any overlap
      return !(sx_field_end < minSx || sx_field > maxSx ||
               sy_field_end < minSy || sy_field > maxSy);
    });
  };

  // Helper: Compute bounding box and relative offsets for a group of fields
  const computeGroupBoundingBox = (selectedFields) => {
    if (selectedFields.length === 0) return null;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    selectedFields.forEach(f => {
      minX = Math.min(minX, f.x_pt);
      minY = Math.min(minY, f.y_pt);
      maxX = Math.max(maxX, f.x_pt + f.width_pt);
      maxY = Math.max(maxY, f.y_pt + f.height_pt);
    });

    return {
      minX,
      minY,
      maxX,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    };
  };

  // Helper: Clamp pasted group to page bounds
  const clampGroupToBounds = (pastedFields, pageH, pageW = 612) => {
    if (pastedFields.length === 0) return pastedFields;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    pastedFields.forEach(f => {
      minX = Math.min(minX, f.x_pt);
      minY = Math.min(minY, f.y_pt);
      maxX = Math.max(maxX, f.x_pt + f.width_pt);
      maxY = Math.max(maxY, f.y_pt + f.height_pt);
    });

    let offsetX = 0, offsetY = 0;

    if (minX < 0) offsetX = -minX;
    if (minY < 0) offsetY = -minY;
    if (maxX > pageW) offsetX = Math.min(offsetX, pageW - maxX);
    if (maxY > pageH) offsetY = Math.min(offsetY, pageH - maxY);

    return pastedFields.map(f => ({
      ...f,
      x_pt: Math.max(0, f.x_pt + offsetX),
      y_pt: Math.max(0, f.y_pt + offsetY),
    }));
  };

  // Check if point is over the resize handle
  // Visual handle is 8x8 at top-right, hitbox is larger for easier activation
  const getResizeHandleAt = (field, sx, sy, zoom) => {
    const pageH = pageHeights[field.page_number] || 792;
    const sx_field = field.x_pt * zoom;
    const sy_field = (pageH - (field.y_pt + field.height_pt)) * zoom;
    const sw = field.width_pt * zoom;
    // eslint-disable-next-line no-unused-vars
    const sh = field.height_pt * zoom;

    const visibleHandleSize = 8;
    const hitboxSize = 40; // Expanded to ensure easy activation

    // Visual handle position: top-right corner, offset inward by 8
    const visibleHandleLeft = sx_field + sw - visibleHandleSize;
    const visibleHandleTop = sy_field - visibleHandleSize;
    const visibleHandleRight = sx_field + sw;
    const visibleHandleBottom = sy_field;

    // Hitbox: centered on the top-right corner with expanded radius
    const centerX = sx_field + sw;
    const centerY = sy_field;
    const hx = centerX - hitboxSize / 2;
    const hy = centerY - hitboxSize / 2;

    // Check if point is in the hitbox
    const inHitbox = sx >= hx && sx <= hx + hitboxSize && sy >= hy && sy <= hy + hitboxSize;

    // Verify visible handle corners are inside hitbox (debug assertion)
    if (inHitbox && process.env.NODE_ENV === 'development') {
      const handleInside =
        visibleHandleLeft >= hx && visibleHandleRight <= hx + hitboxSize &&
        visibleHandleTop >= hy && visibleHandleBottom <= hy + hitboxSize;
      if (!handleInside) {
        console.warn('Resize handle visual not fully inside hitbox - geometry mismatch');
      }
    }

    return inHitbox;
  };

  // Handle keyboard shortcuts (copy, paste, etc.)
  const handleKeyDown = (e) => {
    // Ctrl/Cmd + C: Copy
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault();
      if (selectedFieldIds.length > 0) {
        const selectedFields = fields.filter(f => selectedFieldIds.includes(f.id));
        const boundingBox = computeGroupBoundingBox(selectedFields);

        // Store relative offsets
        const fieldsCopy = selectedFields.map(f => ({
          ...f,
          relX: f.x_pt - boundingBox.minX,
          relY: f.y_pt - boundingBox.minY,
        }));

        setClipboard({ fields: fieldsCopy, boundingBox });
        console.log('Copied', selectedFields.length, 'fields to clipboard');
      }
    }

    // Ctrl/Cmd + V: Paste (on currently active page)
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault();
      if (clipboard && clipboard.fields.length > 0) {
        const targetPageNum = currentPageNum;
        const pageH = pageHeights[targetPageNum] || 792;

        // Create new fields with same relative positions
        const newFields = clipboard.fields.map(f => ({
          id: Date.now() + Math.random(),
          field_key: f.field_key,
          label: f.label,
          render_type: f.render_type,
          page_number: targetPageNum,
          x_pt: f.relX + clipboard.boundingBox.minX,
          y_pt: f.relY + clipboard.boundingBox.minY,
          width_pt: f.width_pt,
          height_pt: f.height_pt,
        }));

        // Clamp to page bounds
        const clampedFields = clampGroupToBounds(newFields, pageH);

        updateFields(prev => {
          const updated = [...prev, ...clampedFields];
          triggerAutosave(updated);
          return updated;
        });

        // Keep pasted group selected
        setSelectedFieldIds(clampedFields.map(f => f.id));
        setSelectedId(null);
        console.log('Pasted', clampedFields.length, 'fields');
      }
    }

    // Escape: Clear selection
    if (e.key === 'Escape') {
      setPlacementMode(null);
      setPlacementCursorPos(null);
      setSelectedFieldIds([]);
      setMarqueeStart(null);
      setMarqueeEnd(null);
    }
  };

  // Canvas handlers
  const makeCanvasMouseDown = (pageNum) => (e) => {
    setCurrentPageNum(pageNum); // Track currently active page
    if (authoring?.is_active || placementMode) return;

    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const r = canvas.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;

    const field = getFieldAtScreenPoint(pageNum, sx, sy);

    // If clicking on a field
    if (field) {
      // Check if clicking on resize handle (allow resizing any field, not just selected ones)
      if (getResizeHandleAt(field, sx, sy, zoom)) {
        setResizingFieldId(field.id);
        setSelectedId(field.id);  // Select the field being resized for visual feedback
        dragStateRef.current = { startX: sx, startY: sy, startWidth: field.width_pt, startHeight: field.height_pt };
        return;
      }

      // If Shift+Click, toggle field in multi-selection
      if (e.shiftKey) {
        setSelectedFieldIds(prev =>
          prev.includes(field.id)
            ? prev.filter(id => id !== field.id)
            : [...prev, field.id]
        );
        setSelectedId(field.id);
        return;
      }

      // Normal click: select and start dragging
      setSelectedFieldIds([]);
      setSelectedId(field.id);
      setDraggedFieldId(field.id);

      const fieldScreenX = field.x_pt * zoom;
      const pageH = pageHeights[pageNum] || 792;
      const fieldScreenY = (pageH - (field.y_pt + field.height_pt)) * zoom;

      setDragOffset({
        x: sx - fieldScreenX,
        y: sy - fieldScreenY,
      });
      dragStateRef.current = { startX: sx, startY: sy };
      return;
    }

    // No field under cursor: Start marquee selection (if not Shift key)
    if (!e.shiftKey) {
      setSelectedFieldIds([]);
      setSelectedId(null);
    }

    setMarqueeStart({ pageNum, sx, sy });
    setMarqueeEnd({ pageNum, sx, sy });
    dragStateRef.current = { isMarquee: true };
  };

  const makeCanvasMouseMove = (pageNum) => (e) => {
    // CRITICAL FIX: Only update state if page actually changed
    // This prevents re-renders on every mousemove
    if (currentPageNum !== pageNum) {
      setCurrentPageNum(pageNum);
    }
    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const r = canvas.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;

    // Track cursor position for click-to-place preview
    if (placementMode) {
      setPlacementCursorPos({
        pageNum,
        sx,
        sy,
      });
    }

    // Track drag preview during active custom drag from sidebar
    if (dragStateStore.isActive && dragStateStore.field && !authoring?.is_active) {
      console.log('🎯 [DRAG PREVIEW] Setting dragPreview:', {
        isActive: dragStateStore.isActive,
        hasField: !!dragStateStore.field,
        authoring_is_active: authoring?.is_active,
        pageNum,
        sx,
        sy,
        fieldLabel: dragStateStore.field.label,
      });
      setDragPreview({
        pageNum,
        sx,
        sy,
        width: dragStateStore.field.width,
        height: dragStateStore.field.height,
        label: dragStateStore.field.label,
      });
    } else if (dragStateStore.isActive || dragStateStore.field) {
      console.log('🎯 [DRAG PREVIEW BLOCKED]:', {
        isActive: dragStateStore.isActive,
        hasField: !!dragStateStore.field,
        authoring_is_active: authoring?.is_active,
        blockReason: authoring?.is_active ? 'authoring.is_active=true' : 'missing drag state',
      });
    }

    // Handle marquee selection
    if (dragStateRef.current?.isMarquee && marqueeStart) {
      setMarqueeEnd({ pageNum, sx, sy });
      const fieldsInMarquee = getFieldsInMarquee(pageNum, marqueeStart, { pageNum, sx, sy });
      setSelectedFieldIds(fieldsInMarquee.map(f => f.id));
      setCanvasCursor('default');
      return;
    }

    // Handle resizing
    if (resizingFieldId && dragStateRef.current) {
      const field = fields.find(f => f.id === resizingFieldId);
      if (field) {
        const deltaX = (sx - dragStateRef.current.startX) / zoom;
        const deltaY = (sy - dragStateRef.current.startY) / zoom;

        // Calculate new dimensions
        const newWidth = Math.max(20, dragStateRef.current.startWidth + deltaX);
        const newHeight = Math.max(12, dragStateRef.current.startHeight - deltaY);

        // CRITICAL FIX: Update in ref, NOT state
        // This prevents trigger of render effect (which depends on fields)
        // and eliminates re-rendering on every mouse move
        dragPreviewFieldsRef.current = fields.map(f =>
          f.id === resizingFieldId
            ? {
                ...f,
                width_pt: newWidth,
                height_pt: newHeight,
              }
            : f
        );

        // Show visual preview on overlay canvas
        setResizePreview({
          pageNum,
          sx: field.x_pt * zoom,
          sy: ((pageHeights[pageNum] || 792) - (field.y_pt + newHeight)) * zoom,
          width: newWidth,
          height: newHeight,
          label: field.label,
        });

        console.log(`📐 [RESIZE] ${field.label}: ${newWidth.toFixed(1)} x ${newHeight.toFixed(1)} pts`);
      }
      setCanvasCursor('nwse-resize');
      return;
    }

    // Handle moving existing field
    if (draggedFieldId && dragStateRef.current) {
      const field = fields.find(f => f.id === draggedFieldId);
      if (!field) return;

      const newScreenX = sx - dragOffset.x;
      const newScreenY = sy - dragOffset.y;

      const [newXPt, newYPt] = screenToPdf(pageNum, newScreenX, newScreenY, null);

      // Update ref with accumulated changes (for eventual application on mouseup)
      dragPreviewFieldsRef.current = fields.map(f =>
        f.id === draggedFieldId
          ? {
              ...f,
              x_pt: Math.max(0, newXPt),
              y_pt: Math.max(0, newYPt),
              page_number: pageNum,
            }
          : f
      );

      // Show visual preview on overlay canvas (like sidebar drag)
      setFieldMovePreview({
        pageNum,
        sx: newScreenX,
        sy: newScreenY,
        width: field.width_pt,
        height: field.height_pt,
        label: field.label,
      });

      console.log(`🎯 [FIELD MOVE] Moving ${field.label} on page ${pageNum}: screen(${newScreenX}, ${newScreenY}) → pdf(${newXPt}, ${newYPt})`);

      setCanvasCursor('grabbing');
      return;
    }

    // Update cursor (only if it changed to avoid re-renders)
    const field = getFieldAtScreenPoint(pageNum, sx, sy);
    let newCursor = placementMode ? 'crosshair' : 'default';

    // Track hovered field for resize handle display
    if (field) {
      setHoveredFieldId(field.id);
      // Show resize cursor on any field with resize handle (not just selected)
      if (getResizeHandleAt(field, sx, sy, zoom)) {
        newCursor = 'nwse-resize';
      } else {
        newCursor = 'grab';
      }
    } else {
      setHoveredFieldId(null);
    }

    if (canvasCursor !== newCursor) {
      setCanvasCursor(newCursor);
    }
  };

  const makeCanvasMouseUp = (pageNum) => (e) => {
    // Finalize marquee selection
    if (dragStateRef.current?.isMarquee) {
      dragStateRef.current = null;
      setMarqueeStart(null);
      setMarqueeEnd(null);
      return;
    }

    // Handle custom drag placement from sidebar
    if (dragStateStore.isActive && dragStateStore.field && !authoring?.is_active) {
      const canvas = canvasRefs.current[pageNum];
      if (canvas) {
        const r = canvas.getBoundingClientRect();
        const sx = e.clientX - r.left;
        const sy = e.clientY - r.top;

        const pageH = pageHeights[pageNum] || 792;
        const widthPt = dragStateStore.field.width;
        const heightPt = dragStateStore.field.height;

        // Cursor position is the visual CENTER of the field (center-feel interaction)
        // Convert to PDF coords: cursor center -> visual bottom-left
        const xCenterPt = sx / zoom;
        const yCenterPt = pageH - (sy / zoom);
        const xPt = xCenterPt - (widthPt / 2);
        const yPt = yCenterPt - (heightPt / 2);

        // Bottom-left anchor: computed from visual center
        const newField = {
          id: Date.now(),
          field_key: dragStateStore.field.field_key,
          page_number: pageNum,
          x_pt: Math.max(0, xPt),
          y_pt: Math.max(0, yPt),
          width_pt: widthPt,
          height_pt: heightPt,
        };

        // Add label and render_type from dragStateStore
        newField.label = dragStateStore.field.label;
        newField.render_type = dragStateStore.field.render_type;

        console.log('📌 Placed field from drag-from-sidebar:', newField.field_key, 'on page', pageNum);

        // Clamp field to page bounds before saving
        const pageW = 612; // Standard US letter width in points
        const clampedField = clampGroupToBounds([newField], pageH, pageW)[0];

        updateFields(prev => {
          const updated = [...prev, clampedField];
          console.log(`➕ [FIELD PLACED - DRAG DROP] page=${clampedField.page_number}, total fields now=${updated.length}`);
          triggerAutosave(updated);
          return updated;
        });
        setSelectedId(clampedField.id);
        setDragPreview(null);
      }

      // Clear custom drag state
      dragStateStore.isActive = false;
      dragStateStore.field = null;
    }

    // CRITICAL FIX: Apply drag preview changes to actual state now that drag ended
    // During drag, we updated dragPreviewFieldsRef (not state) to avoid render effect triggers
    // Now apply those changes and trigger autosave
    if ((draggedFieldId || resizingFieldId) && dragPreviewFieldsRef.current) {
      updateFields(dragPreviewFieldsRef.current);
      triggerAutosave(dragPreviewFieldsRef.current);
      dragPreviewFieldsRef.current = null;
    }

    // Clear field movement/resize state AND visual previews
    setDraggedFieldId(null);
    setResizingFieldId(null);
    setFieldMovePreview(null);
    setResizePreview(null);
    dragStateRef.current = null;
    setCanvasCursor('default');
  };

  // Right-click for context menu
  const makeCanvasContextMenu = (pageNum) => (e) => {
    setCurrentPageNum(pageNum); // Track currently active page
    e.preventDefault();
    if (authoring?.is_active) return;

    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const r = canvas.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;

    const field = getFieldAtScreenPoint(pageNum, sx, sy);
    if (field) {
      setSelectedId(field.id);
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        fieldId: field.id,
        pageNum,
      });
    } else {
      // Right-click on empty space: show paste option if clipboard available
      if (clipboard) {
        setContextMenu({
          x: e.clientX,
          y: e.clientY,
          pageNum,
        });
      }
    }
  };

  // Click-to-place
  const makeCanvasClick = (pageNum) => (e) => {
    if (!placementMode) return;

    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const r = canvas.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;

    const pageH = pageHeights[pageNum] || 792;
    const widthPt = placementMode.render_type === 'checkbox' ? 5 : DEFAULT_WIDTH;
    const heightPt = placementMode.render_type === 'checkbox' ? 10 : DEFAULT_HEIGHT;

    // Cursor position is the visual CENTER of the field (center-feel interaction)
    // Convert to PDF coords: cursor center -> visual bottom-left
    const xCenterPt = sx / zoom;
    const yCenterPt = pageH - (sy / zoom);
    const xPt = xCenterPt - (widthPt / 2);
    const yPt = yCenterPt - (heightPt / 2);

    // Bottom-left anchor: computed from visual center
    const newField = {
      id: Date.now(),
      field_key: placementMode.field_key,
      label: placementMode.label,  // Store label for display
      render_type: placementMode.render_type,  // Store render_type for styling
      page_number: pageNum,
      x_pt: Math.max(0, xPt),
      y_pt: Math.max(0, yPt), // Bottom-left anchor computed from visual center
      width_pt: widthPt,
      height_pt: heightPt,
    };

    console.log('📌 Placed field from click-to-place:', newField.field_key, 'on page', pageNum);

    // Clamp field to page bounds before saving
    const pageWidthClick = 612; // Standard US letter width in points
    const clampedClickField = clampGroupToBounds([newField], pageH, pageWidthClick)[0];

    updateFields(prev => {
      const updated = [...prev, clampedClickField];
      triggerAutosave(updated);
      return updated;
    });
    setPlacementMode(null);
    setPlacementCursorPos(null);
    setSelectedId(clampedClickField.id);
  };


  // Drag from sidebar - just handle drag mechanics (preview handled by mousemove)
  const makeDragOver = (pageNum) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Preview is now updated via mousemove handler when isActiveDragRef is true
  };

  // Drag leave - no longer needed, handled by global drag tracking
  const makeDragLeave = (pageNum) => (e) => {
    // Global dragend listener handles preview cleanup
  };

  const makeDrop = (pageNum) => (e) => {
    e.preventDefault();
    if (!pdfDoc || authoring?.is_active) return;

    // Clear drag preview
    setDragPreview(null);

    // Handle custom drag (from sidebar with dragStateStore)
    if (dragStateStore.isActive && dragStateStore.field) {
      const field = dragStateStore.field;
      const canvas = canvasRefs.current[pageNum];
      if (!canvas) return;

      const r = canvas.getBoundingClientRect();
      const sx = e.clientX - r.left;
      const sy = e.clientY - r.top;

      const pageH = pageHeights[pageNum] || 792;
      const widthPt = field.width;
      const heightPt = field.height;

      // Cursor position is the visual CENTER of the field (center-feel interaction)
      // Convert to PDF coords: cursor center -> visual bottom-left
      const xCenterPt = sx / zoom;
      const yCenterPt = pageH - (sy / zoom);
      const xPt = xCenterPt - (widthPt / 2);
      const yPt = yCenterPt - (heightPt / 2);

      // Bottom-left anchor: computed from visual center
      const newField = {
        id: Date.now(),
        field_key: field.field_key,
        label: field.label,  // Store label for display
        render_type: field.render_type,  // Store render_type for styling
        page_number: pageNum,
        x_pt: Math.max(0, xPt),
        y_pt: Math.max(0, yPt), // Bottom-left anchor computed from visual center
        width_pt: widthPt,
        height_pt: heightPt,
      };

      // Clamp field to page bounds before saving
      const pageWidthDrop = 612; // Standard US letter width in points
      const clampedDropField = clampGroupToBounds([newField], pageH, pageWidthDrop)[0];

      updateFields(prev => {
        const updated = [...prev, clampedDropField];
        triggerAutosave(updated);
        return updated;
      });
      setSelectedId(clampedDropField.id);
      setPlacementMode(null);

      // Clear custom drag state
      dragStateStore.isActive = false;
      dragStateStore.field = null;
      return;
    }

    // Handle native drag (fallback, if dataTransfer has data)
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const canvas = canvasRefs.current[pageNum];
      if (!canvas) return;

      const r = canvas.getBoundingClientRect();
      const sx = e.clientX - r.left;
      const sy = e.clientY - r.top;

      const pageH = pageHeights[pageNum] || 792;
      const widthPt = data.render_type === 'checkbox' ? 5 : DEFAULT_WIDTH;
      const heightPt = data.render_type === 'checkbox' ? 10 : DEFAULT_HEIGHT;

      // Cursor position is the visual CENTER of the field (center-feel interaction)
      // Convert to PDF coords: cursor center -> visual bottom-left
      const xCenterPt = sx / zoom;
      const yCenterPt = pageH - (sy / zoom);
      const xPt = xCenterPt - (widthPt / 2);
      const yPt = yCenterPt - (heightPt / 2);

      // Bottom-left anchor: computed from visual center
      const newField = {
        id: Date.now(),
        field_key: data.field_key,
        label: data.label || data.field_key,  // Store label for display
        render_type: data.render_type,  // Store render_type for styling
        page_number: pageNum,
        x_pt: Math.max(0, xPt),
        y_pt: Math.max(0, yPt), // Bottom-left anchor computed from visual center
        width_pt: widthPt,
        height_pt: heightPt,
      };

      // Clamp field to page bounds before saving
      const pageWidthNative = 612; // Standard US letter width in points
      const clampedNativeField = clampGroupToBounds([newField], pageH, pageWidthNative)[0];

      updateFields(prev => {
        const updated = [...prev, clampedNativeField];
        triggerAutosave(updated);
        return updated;
      });
      setSelectedId(clampedNativeField.id);
      setPlacementMode(null);
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const handleFieldAddFromSidebar = (field) => {
    // Enter click-to-place mode
    if (!pdfDoc || authoring?.is_active) return;
    setPlacementMode({
      field_key: field.field_key,
      label: field.label,
      render_type: field.render_type,
    });
  };

  const handleDeleteField = (fieldId) => {
    updateFields(prev => {
      const updated = prev.filter(f => f.id !== fieldId);
      triggerAutosave(updated);
      return updated;
    });
    setSelectedId(null);
    setContextMenu(null);
  };

  // eslint-disable-next-line no-unused-vars
  const handleSaveAll = async () => {
    if (!fields.length) {
      alert('Add fields first');
      return;
    }

    setSaving(true);
    try {
      const payload = fields.map(f => ({
        field_key: f.field_key,
        page_number: f.page_number,
        x_pt: f.x_pt,
        y_pt: f.y_pt,
        width_pt: f.width_pt,
        height_pt: f.height_pt,
      }));

      const res = await axios.put(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}/authoring`,
        { fields: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAuthoring(res.data);
      setFields(res.data.fields);  // Server confirmation, don't add to history
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleActivate = async () => {
    if (!fields.length) {
      alert('Need at least 1 field');
      return;
    }

    if (!window.confirm('Activate? Will be immutable.')) return;

    setActivating(true);
    try {
      await axios.post(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const res = await axios.get(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}/authoring`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAuthoring(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Activate failed');
    } finally {
      setActivating(false);
    }
  };

  // Upload handler
  const handleUpload = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}/upload-pdf`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAuthoring(res.data);
      setTemplateType(res.data.template_type);
      setFields(res.data.fields);  // PDF upload confirmation, don't add to history

      const heights = {};
      res.data.pages.forEach(p => heights[p.page_number] = p.height_pt);
      setPageHeights(heights);

      if (res.data.pdf_file_path) {
        await loadPDF(res.data.pdf_file_path);
      }

      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || `Upload failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Version name handler
  const handleSaveVersionName = async () => {
    if (!versionNameInput.trim() && !authoring.version_name) {
      // Allow clearing the name
      setEditingVersionName(false);
      return;
    }

    setUpdatingVersionName(true);
    try {
      const payload = {
        version_name: versionNameInput || null,
      };
      const res = await axios.patch(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAuthoring(prev => ({
        ...prev,
        version_name: res.data.version_name,
      }));
      setEditingVersionName(false);
      setVersionNameInput('');
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save version name');
      console.error('Version name save failed:', e);
    } finally {
      setUpdatingVersionName(false);
    }
  };

  // Archive version handler
  const handleArchiveVersion = async () => {
    setUpdatingArchive(true);
    try {
      const payload = { is_archived: true };
      const res = await axios.patch(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAuthoring(prev => ({
        ...prev,
        is_archived: res.data.is_archived,
      }));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to archive version');
      console.error('Archive failed:', e);
    } finally {
      setUpdatingArchive(false);
    }
  };

  // Unarchive version handler
  const handleUnarchiveVersion = async () => {
    setUpdatingArchive(true);
    try {
      const payload = { is_archived: false };
      const res = await axios.patch(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAuthoring(prev => ({
        ...prev,
        is_archived: res.data.is_archived,
      }));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to unarchive version');
      console.error('Unarchive failed:', e);
    } finally {
      setUpdatingArchive(false);
    }
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: '#d32f2f' }}>Error: {error}</div>;
  if (!authoring) return <div style={{ padding: '20px' }}>No data</div>;

  // eslint-disable-next-line no-unused-vars
  const hasPDF = authoring.pdf_file_path && pdfDoc;
  const pageCount = Object.keys(pageHeights).length;
  // Show editor UI if we have PDF data, even if pdfDoc is still loading
  const showEditor = authoring?.pdf_file_path;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', gap: '0', padding: '0' }}>
      {/* Field Catalog Sidebar */}
      <div style={{ width: '280px', display: 'flex', flexDirection: 'column', borderRight: '1px solid #ddd', overflow: 'hidden' }}>
        {templateType && (
          <FieldCatalogSidebar
            templateCategory={templateType}
            token={token}
            onFieldAdd={handleFieldAddFromSidebar}
          />
        )}
      </div>

      {/* PDF Editor Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: 'none' }}>
        {!showEditor ? (
          <PDFUploadArea onUpload={handleUpload} />
        ) : (
          <>
            {/* Toolbar */}
            <div style={{ padding: '12px', borderBottom: '1px solid #ddd', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <select value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}>
                <option value={0.5}>50%</option>
                <option value={1}>100%</option>
                <option value={1.5}>150%</option>
                <option value={2}>200%</option>
              </select>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {Math.round(zoom * 100)}% | {pageCount} pages
              </span>
              {placementMode && (
                <span style={{ fontSize: '12px', color: '#ff6b6b', fontWeight: 'bold' }}>
                  Placing: {placementMode.label} (click to place, ESC to cancel)
                </span>
              )}
              {autoSaveStatus && (
                <span style={{ fontSize: '12px', color: '#115845', fontWeight: '500' }}>
                  {autoSaveStatus === 'saving' ? 'Saving...' : 'Saved'}
                </span>
              )}
            </div>

            {/* Scrollable Page Container */}
            <div
              ref={containerRef}
              tabIndex={0}
              style={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px',
                gap: '20px',
                outline: 'none',
              }}
              onKeyDown={handleKeyDown}
            >
              {pdfDoc && Array.from({ length: pageCount }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <div
                    key={pageNum}
                    style={{
                      position: 'relative',
                      border: '1px solid #999',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    {/* Main PDF canvas */}
                    <canvas
                      ref={(el) => {
                        if (el) canvasRefs.current[pageNum] = el;
                      }}
                      onMouseDown={makeCanvasMouseDown(pageNum)}
                      onMouseMove={makeCanvasMouseMove(pageNum)}
                      onMouseUp={makeCanvasMouseUp(pageNum)}
                      onMouseLeave={(e) => {
                        setHoveredFieldId(null);
                        makeCanvasMouseUp(pageNum)(e);
                      }}
                      onDragOver={makeDragOver(pageNum)}
                      onDragLeave={makeDragLeave(pageNum)}
                      onDrop={makeDrop(pageNum)}
                      onContextMenu={makeCanvasContextMenu(pageNum)}
                      onClick={makeCanvasClick(pageNum)}
                      style={{
                        cursor: canvasCursor,
                        display: 'block',
                      }}
                    />
                    {/* Overlay canvas for transient elements (previews, marquee) */}
                    <canvas
                      ref={(el) => {
                        if (el) overlayCanvasRefs.current[pageNum] = el;
                      }}
                      style={{
                        cursor: canvasCursor,
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none', // Don't intercept events
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Context Menu */}
            {contextMenu && (
              <div
                style={{
                  position: 'fixed',
                  left: `${contextMenu.x}px`,
                  top: `${contextMenu.y}px`,
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                }}
                onClick={() => setContextMenu(null)}
              >
                {contextMenu.fieldId && (
                  <>
                    <button
                      onClick={() => {
                        // Copy either multi-selected fields or just the right-clicked field
                        const fieldsToCopy = selectedFieldIds.length > 0
                          ? fields.filter(f => selectedFieldIds.includes(f.id))
                          : [fields.find(f => f.id === contextMenu.fieldId)].filter(Boolean);

                        if (fieldsToCopy.length === 0) return;

                        const boundingBox = computeGroupBoundingBox(fieldsToCopy);
                        const fieldsCopy = fieldsToCopy.map(f => ({
                          ...f,
                          relX: f.x_pt - boundingBox.minX,
                          relY: f.y_pt - boundingBox.minY,
                        }));
                        setClipboard({ fields: fieldsCopy, boundingBox });
                        setContextMenu(null);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: '#333',
                      }}
                      onMouseEnter={(e) => (e.target.style.backgroundColor = '#f5f5f5')}
                      onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                    >
                      Copy{selectedFieldIds.length > 0 ? ` (${selectedFieldIds.length})` : ''}
                    </button>
                    <button
                      onClick={() => {
                        handleDeleteField(contextMenu.fieldId);
                        setContextMenu(null);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: '#d32f2f',
                      }}
                      onMouseEnter={(e) => (e.target.style.backgroundColor = '#ffebee')}
                      onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                    >
                      Delete
                    </button>
                  </>
                )}
                {clipboard && !contextMenu.fieldId && (
                  <button
                    onClick={() => {
                      const targetPageNum = contextMenu.pageNum || 1;
                      const pageH = pageHeights[targetPageNum] || 792;

                      const newFields = clipboard.fields.map(f => ({
                        id: Date.now() + Math.random(),
                        field_key: f.field_key,
                        label: f.label,
                        render_type: f.render_type,
                        page_number: targetPageNum,
                        x_pt: f.relX + clipboard.boundingBox.minX,
                        y_pt: f.relY + clipboard.boundingBox.minY,
                        width_pt: f.width_pt,
                        height_pt: f.height_pt,
                      }));

                      const clampedFields = clampGroupToBounds(newFields, pageH);

                      updateFields(prev => {
                        const updated = [...prev, ...clampedFields];
                        triggerAutosave(updated);
                        return updated;
                      });

                      setSelectedFieldIds(clampedFields.map(f => f.id));
                      setSelectedId(null);
                      setContextMenu(null);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 16px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: '#333',
                    }}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                  >
                    Paste Here
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Right Sidebar: Clean, integrated version and rules */}
      <div style={{ width: '320px', display: 'flex', flexDirection: 'column', gap: '0', padding: '12px', borderLeft: '1px solid #ddd', overflow: 'auto' }}>
        {/* Version Header - Minimal */}
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #ddd', marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#666' }}>Version</div>

          {/* Version Name - Editable */}
          {editingVersionName ? (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
              <input
                type="text"
                value={versionNameInput}
                onChange={(e) => setVersionNameInput(e.target.value)}
                placeholder="Enter version name..."
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontFamily: 'inherit',
                }}
                disabled={updatingVersionName}
                autoFocus
              />
              <button
                onClick={handleSaveVersionName}
                disabled={updatingVersionName}
                style={{
                  padding: '6px 8px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: updatingVersionName ? 0.6 : 1,
                }}
              >
                {updatingVersionName ? '...' : 'Save'}
              </button>
            </div>
          ) : (
            <div
              onClick={() => {
                setEditingVersionName(true);
                setVersionNameInput(authoring.version_name || '');
              }}
              style={{
                padding: '6px 8px',
                border: '1px solid #ddd',
                borderRadius: '3px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '12px',
                color: authoring.version_name ? '#333' : '#999',
                minHeight: '18px',
                marginBottom: '8px',
              }}
            >
              {authoring.version_name || '(Click to name)'}
            </div>
          )}

          {/* Archive / Unarchive */}
          <button
            onClick={authoring.is_archived ? handleUnarchiveVersion : handleArchiveVersion}
            disabled={updatingArchive}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: authoring.is_archived ? '#4CAF50' : '#ff9800',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              opacity: updatingArchive ? 0.6 : 1,
            }}
          >
            {updatingArchive ? '...' : authoring.is_archived ? 'Unarchive' : 'Archive'}
          </button>
        </div>

        {/* Rules - Integrated directly (no container) */}
        {templateType && (
          <div style={{ paddingBottom: '12px', borderBottom: '1px solid #ddd', marginBottom: '12px' }}>
            <VersionRulesPanel
              templateId={templateId}
              versionId={versionId}
              templateType={templateType}
              token={token}
              authoring={authoring}
              onRulesSaved={handleRulesSaved}
            />
          </div>
        )}

        {/* Status - Lightweight */}
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #ddd', marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
            {authoring.is_active ? <span style={{ color: '#4CAF50', fontWeight: '600' }}>ACTIVE</span> : <span style={{ color: '#999', fontWeight: '600' }}>DRAFT</span>}
            {' '} • {fields.length} fields
          </div>
          {placementMode && <div style={{ fontSize: '11px', color: '#ff6b6b', fontWeight: 'bold' }}>Placement Mode ON</div>}
        </div>

        {/* Fields List - Minimal */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Fields ({fields.length})
          </div>
          {fields.length === 0 ? (
            <div style={{ fontSize: '11px', color: '#999', textAlign: 'center', padding: '12px 0' }}>No fields</div>
          ) : (
            fields.map(f => (
              <div
                key={f.id}
                onClick={() => setSelectedId(f.id)}
                style={{
                  padding: '6px 8px',
                  border: selectedId === f.id ? '2px solid #ffd700' : '1px solid #ddd',
                  margin: '6px 0',
                  cursor: 'pointer',
                  background: selectedId === f.id ? '#fff9e6' : 'white',
                  borderRadius: '3px',
                  fontSize: '11px',
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '11px' }}>{f.field_key}</div>
                <div style={{ fontSize: '10px', color: '#999' }}>p{f.page_number}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
